import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import {
    CreateGameAdminCommentDto,
    GameAdminCommentDto,
    UpdateGameAdminCommentDto,
} from '../../../models/game/dto/game-admin-comment.dto';

// Get admin comments for a game
export const useGetGameAdminComments = (gameId: number) => {
    const apiClient = useApiClient();
    return useQuery({
        queryKey: ['game-admin-comments', gameId],
        queryFn: async (): Promise<GameAdminCommentDto[]> => {
            return apiClient.fetchApi<GameAdminCommentDto[], {}>({ method: 'GET', endpoint: `games/${gameId}/admin-comments` });
        },
        enabled: !!gameId,
    });
};

// Create a new admin comment
export const useCreateGameAdminComment = (gameId: number) => {
    const queryClient = useQueryClient();
    const toast = useToast();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async (commentData: CreateGameAdminCommentDto): Promise<GameAdminCommentDto> => {
            return apiClient.fetchApi<GameAdminCommentDto, CreateGameAdminCommentDto>({ method: 'POST', endpoint: `games/${gameId}/admin-comments`, body: commentData });
        },
        onSuccess: () => {
            // Invalidate and refetch comments
            queryClient.invalidateQueries({ queryKey: ['game-admin-comments', gameId] });
            // Also invalidate game details to refresh the comments count
            queryClient.invalidateQueries({ queryKey: ['game', gameId] });

            toast({
                title: translate('admin_comments.created_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (error: Error) => {
            toast({
                title: translate('admin_comments.create_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};

// Update an admin comment
export const useUpdateGameAdminComment = (gameId: number) => {
    const queryClient = useQueryClient();
    const toast = useToast();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async ({ commentId, data }: { commentId: number; data: UpdateGameAdminCommentDto }): Promise<GameAdminCommentDto> => {
            return apiClient.fetchApi<GameAdminCommentDto, UpdateGameAdminCommentDto>({ method: 'PUT', endpoint: `games/${gameId}/admin-comments/${commentId}`, body: data });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['game-admin-comments', gameId] });

            toast({
                title: translate('admin_comments.updated_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (error: Error) => {
            toast({
                title: translate('admin_comments.update_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};

// Delete an admin comment
export const useDeleteGameAdminComment = (gameId: number) => {
    const queryClient = useQueryClient();
    const toast = useToast();
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (commentId: number): Promise<{ success: boolean }> => {
            return apiClient.fetchApi<{ success: boolean }, {}>({ method: 'DELETE', endpoint: `games/${gameId}/admin-comments/${commentId}` });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['game-admin-comments', gameId] });
            queryClient.invalidateQueries({ queryKey: ['game', gameId] });

            toast({
                title: translate('admin_comments.deleted_successfully'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (error: Error) => {
            toast({
                title: translate('admin_comments.delete_error'),
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
